import { Localized, useLocalization } from "@fluent/react";
import {
    Alert,
    Box, Button, Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Stack,
    TextField, Typography
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { AppId } from "../../../../app/appTypes";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { SaveButton } from "../../../../app/components/SaveButton";
import { CancelButton } from "../../../../app/components/CancelButton";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import { unwrapResult } from "@reduxjs/toolkit";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CommentIcon from '@mui/icons-material/Comment';
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
    DocumentQuestion,
    addDocumentAnalyzedObject,
    CreateUpdateDocumentAnalyzedObjectPayload,
    DocumentQuestionAnswerPayload,
    DocumentAnswerType,
    DocumentAnalyzedObject,
    updateDocumentAnalyzedObject,
} from "../documentQuestionsApi";
import { LpField } from "../../../../app/components/LpField";
import { RequiredError } from "../../../../app/components/RequiredError";
import { isDocEditable } from "../../documentSlice";
import { SectionComments } from "../../SectionComments";
import { LoadingButton } from "@mui/lab";
import produce from "immer";
import { RichTextEditor } from "../../../../app/RichTextEditor";

export interface DocumentQuestionAnswer {
    id: number;
    questionId: number;
    value: string;
    analyzedObjectId?: number;
}

export interface AddEditAnalyzedObjectProps {
    documentId: AppId;
    sectionId: string;
    analyzedObjectRow: DocumentAnalyzedObject | null;
    questionsDefinitions: DocumentQuestion[];
    mode: "create" | "edit" | "comment";
    onSuccessfulSave: (analyzedObject: DocumentAnalyzedObject, close: boolean) => void;
    onCancelClick: () => void;
    next: (analyzedObject: DocumentAnalyzedObject) => void;
    back: (analyzedObject: DocumentAnalyzedObject) => void;
    lastItem: boolean;
    index: number | null
    total: number
}

function ResultChips({
    value,
    onChange,
}: {
    value: DocumentAnswerType;
    onChange: (v: DocumentAnswerType) => void;
}) {
    const isEditable = useAppSelector(isDocEditable)

    return (
        <Stack direction="row" spacing={1}>
            <Chip
                label={<Localized id="yes">_yes</Localized>}
                clickable={isEditable}
                disabled={!isEditable}
                color={value === "YES" ? "success" : "default"}
                variant={value === "YES" ? "filled" : "outlined"}
                onClick={() => onChange("YES")}
            />
            <Chip
                label={<Localized id="no">_no</Localized>}
                clickable={isEditable}
                disabled={!isEditable}
                color={value === "NO" ? "error" : "default"}
                variant={value === "NO" ? "filled" : "outlined"}
                onClick={() => onChange("NO")}
            />
            <Chip
                label={<Localized id="not-applicable-short">_not applicable</Localized>}
                clickable={isEditable}
                disabled={!isEditable}
                color={value === "NA" ? "primary" : "default"}
                variant={value === "NA" ? "filled" : "outlined"}
                onClick={() => onChange("NA")}
            />
        </Stack>
    );
}

export const AddEditAnalyzedObject = ({
    documentId,
    sectionId,
    analyzedObjectRow,
    questionsDefinitions,
    mode,
    onCancelClick,
    onSuccessfulSave,
    next,
    back,
    lastItem,
    index,
    total,
}: AddEditAnalyzedObjectProps) => {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");
    const [name, setName] = useState<string>('');
    const [description, setDescription] = useState<string>(analyzedObjectRow?.description ?? "");
    const [comment, setComment] = useState<string>(analyzedObjectRow?.resultComment ?? "");
    const [showDescriptionEditor, setShowDescriptionEditor] = useState(false);
    const [validationErrors, setValidationErrors] = useState(false)
    const nameError = validationErrors && name === ""
    const isCommentable = mode === 'comment'
    const isEditable = useAppSelector(isDocEditable)

    const [finalAssessment, setFinalAssessment] = useState<DocumentAnswerType>('NONE');

    useEffect(() => {
        setFinalAssessment(analyzedObjectRow?.result ?? "NONE");
        setName(analyzedObjectRow?.name || "")
        setDescription(analyzedObjectRow?.description ?? "")
        setShowDescriptionEditor(false)
    }, [analyzedObjectRow]);

    const [answers, setAnswers] = useState<Record<number, DocumentAnswerType>>({});

    useEffect(() => {
        setAnswers(() => {
            if (!analyzedObjectRow?.answers) return {};
            const map: Record<number, DocumentAnswerType> = {};
            for (const a of analyzedObjectRow.answers) {
                const v = (a.value as DocumentAnswerType) ?? "NONE";
                map[a.questionId] = v === "YES" || v === "NO" || v === "NA" || v === "NONE" ? v : "NONE";
            }
            return map;
        });
    }, [analyzedObjectRow]);

    const handleAllYesClick = () => {
        setAnswers(produce(draft => {
            for (let i = 0; i < questionsDefinitions.length; i++) {
                const q = questionsDefinitions[i];
                draft[q.id] = "YES"
            }
        }))
        setFinalAssessment('YES')
    }

    const handleAllNoClick = () => {
        setAnswers(produce(draft => {
            for (let i = 0; i < questionsDefinitions.length; i++) {
                const q = questionsDefinitions[i];
                draft[q.id] = "NO"
            }
        }))
        setFinalAssessment('NO')
    }

    const handleAllNotApplicableClick = () => {
        setAnswers(produce(draft => {
            for (let i = 0; i < questionsDefinitions.length; i++) {
                const q = questionsDefinitions[i];
                draft[q.id] = "NA"
            }
        }))
        setFinalAssessment('NA')
    }

    const questionRows = useMemo(
        () => [...questionsDefinitions].sort((a, b) => a.order - b.order),
        [questionsDefinitions]
    );

    const answeredCount = useMemo(
        () => questionRows.filter(q => {
            const value = answers[q.id]
            return value === "YES" || value === "NO" || value === "NA"
        }).length,
        [questionRows, answers]
    )

    const descriptionPreview = useMemo(
        () => description
            .replace(/<[^>]*>/g, " ")
            .replace(/&nbsp;/g, " ")
            .replace(/\s+/g, " ")
            .trim(),
        [description]
    )

    const saveAndClose = async () => {
        setValidationErrors(true)
        if (!name?.trim()) {
            return;
        }

        setSaving(true);
        setServerError("");

        const findExistingAnswerId = (qid: number): number | undefined =>
            analyzedObjectRow?.answers?.find(a => a.questionId === qid)?.id;

        const answersPayload: DocumentQuestionAnswerPayload[] = Object.entries(answers).map(
            ([qidStr, val]) => {
                const qid = Number(qidStr);
                const existingId = findExistingAnswerId(qid);
                return {
                    id: existingId ?? null,
                    questionId: qid,
                    value: val,
                };
            }
        );

        const payload: CreateUpdateDocumentAnalyzedObjectPayload = {
            sectionId: sectionId,
            name: name.trim(),
            description: description || "",
            objectType: analyzedObjectRow?.objectType ?? "COMMON",
            result: finalAssessment,
            resultComment: comment || "",
            parentId: null,
            answers: answersPayload,
        };

        try {
            let result: DocumentAnalyzedObject;
            if (mode === "create") {
                result = result = await dispatch(
                    addDocumentAnalyzedObject({ documentId, sectionId, payload })
                ).then(unwrapResult) as DocumentAnalyzedObject;
                onSuccessfulSave(result, true);
            } else {
                if (!analyzedObjectRow?.id) {
                    setServerError("Brak ID obiektu do edycji.");
                    return;
                }
                result = await dispatch(
                    updateDocumentAnalyzedObject({ documentId, sectionId, objectId: analyzedObjectRow.id, payload })
                ).then(unwrapResult) as DocumentAnalyzedObject;

                onSuccessfulSave(result, true);
            }
            dispatch(showSuccess("saved"));
        } catch (e: any) {
            dispatch(showError("error"));
            const msg =
                e?.payload?.problem?.title ??
                e?.message ??
                "Wystąpił błąd podczas zapisu.";
            setServerError(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleBackClick = () => {
        if (analyzedObjectRow) {
            back(analyzedObjectRow)
        }
    }

    const handleSaveAndNext = async () => {
        setValidationErrors(true)
        if (!name?.trim()) {
            return;
        }

        setSaving(true);
        setServerError("");

        const findExistingAnswerId = (qid: number): number | undefined =>
            analyzedObjectRow?.answers?.find(a => a.questionId === qid)?.id;

        const answersPayload: DocumentQuestionAnswerPayload[] = Object.entries(answers).map(
            ([qidStr, val]) => {
                const qid = Number(qidStr);
                const existingId = findExistingAnswerId(qid);
                return {
                    id: existingId ?? null,
                    questionId: qid,
                    value: val,
                };
            }
        );

        const payload: CreateUpdateDocumentAnalyzedObjectPayload = {
            sectionId: sectionId,
            name: name.trim(),
            description: description || "",
            objectType: analyzedObjectRow?.objectType ?? "COMMON",
            result: finalAssessment,
            resultComment: comment || "",
            parentId: null,
            answers: answersPayload,
        };

        try {
            let result: DocumentAnalyzedObject;
            if (mode === "create") {
                result = result = await dispatch(
                    addDocumentAnalyzedObject({ documentId, sectionId, payload })
                ).then(unwrapResult) as DocumentAnalyzedObject;
            } else {
                if (!analyzedObjectRow?.id) {
                    setServerError("Brak ID obiektu do edycji.");
                    return;
                }
                result = await dispatch(
                    updateDocumentAnalyzedObject({ documentId, sectionId, objectId: analyzedObjectRow.id, payload })
                ).then(unwrapResult) as DocumentAnalyzedObject;

                onSuccessfulSave(result, false);

                if (analyzedObjectRow) {
                    next(analyzedObjectRow);
                }
            }
            dispatch(showSuccess("saved"));
        } catch (e: any) {
            dispatch(showError("error"));
            const msg =
                e?.payload?.problem?.title ??
                e?.message ??
                "Wystąpił błąd podczas zapisu.";
            setServerError(msg);
        } finally {
            setSaving(false);
        }
    };

    const answer = (a: DocumentAnswerType, qId: string) => {
        setAnswers(produce(draft => {
            draft[qId] = a
        }))
        switch (a) {
            case 'NO':
                setFinalAssessment('NO')
                break
            case 'NA':
            case 'YES':
                if (questionsDefinitions.every(q => answers[q.id] === a || q.id === qId)) {
                    setFinalAssessment(a)
                }
                break
            case 'NONE':
                break
        }
    }

    let title = <></>
    switch (mode) {
        case "create":
            title = (
                <Box sx={{ display: "inline-flex", alignItems: "center", lineHeight: 1.2 }}>
                    <AddCircleIcon fontSize="small" sx={{ mr: 1 }} color="success" />
                    <Localized id="document-analyzed-objects-add">
                        <span>_Dodaj obiekt</span>
                    </Localized>
                </Box>
            )
            break;
        case "edit":
            title = (
                <Box sx={{ display: "inline-flex", alignItems: "center", lineHeight: 1.2 }}>
                    <EditIcon fontSize="small" sx={{ mr: 1 }} color="info" />
                    <Localized id="document-analyzed-objects-evaluate">
                        <span>_evaluate</span>
                    </Localized>
                </Box>
            )
            break;
        case "comment":
            title = (
                <Box sx={{ display: "inline-flex", alignItems: "center", lineHeight: 1.2 }}>
                    <CommentIcon fontSize="small" sx={{ mr: 1 }} color="info" />
                    <Localized id="comments">
                        <span>_Komentarze</span>
                    </Localized>
                </Box>
            )
            break;
    }

    const content = <>
        <Box
            sx={{
                mb: 1,
            }}
        >
            <Box
                sx={{
                    p: 1,
                    pt: 0.5,
                    pb: 0.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "grey.100",
                    mb: 1,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {l10n.getString("document-analyzed-object-title", null, "_Analizowany obiekt")}
                </Typography>
            </Box>
            <TextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!isEditable}
                fullWidth
                margin="dense"
                size="small"
                required
                error={nameError}
                helperText={nameError && <RequiredError />}
                variant="outlined"
                label={l10n.getString("name", null, "_name")}
                sx={{ mt: 0.25 }}
            />
            <Box
                sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "6px",
                    overflow: "hidden",
                    bgcolor: "background.paper",
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                        px: 1.5,
                        py: 0.75,
                        borderBottom: showDescriptionEditor ? "1px solid" : "none",
                        borderColor: "divider",
                    }}
                >
                    <Typography
                        variant="body2"
                        color={
                            showDescriptionEditor || !descriptionPreview
                                ? "text.secondary"
                                : "text.primary"
                        }
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            lineHeight: 1.6,
                            fontWeight: showDescriptionEditor ? 600 : 400,
                            fontStyle:
                                !showDescriptionEditor && !descriptionPreview
                                    ? "italic"
                                    : "normal",
                            overflowWrap: "anywhere",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }}
                    >
                        {showDescriptionEditor
                            ? l10n.getString("description", null, "Opis")
                            : descriptionPreview ||
                            l10n.getString(
                                "document-description-empty",
                                null,
                                "Brak opisu"
                            )}
                    </Typography>

                    {isEditable && (
                        <Button
                            size="small"
                            color="inherit"
                            startIcon={
                                showDescriptionEditor
                                    ? <ExpandLessIcon />
                                    : <EditOutlinedIcon />
                            }
                            onClick={() => setShowDescriptionEditor(v => !v)}
                            aria-expanded={showDescriptionEditor}
                            sx={{
                                flexShrink: 0,
                                minWidth: 0,
                                px: 1,
                                py: 0.25,
                                fontSize: 12,
                                fontWeight: 500,
                                textTransform: "none",
                                color: "text.secondary",
                                borderRadius: "4px",
                                "& .MuiButton-startIcon": {
                                    mr: 0.5,
                                },
                                "& .MuiButton-startIcon > *:nth-of-type(1)": {
                                    fontSize: 16,
                                },
                                "&:hover": {
                                    color: "primary.main",
                                    bgcolor: "action.hover",
                                },
                            }}
                        >
                            {showDescriptionEditor
                                ? l10n.getString("collapse", null, "Zwiń")
                                : l10n.getString("edit", null, "Edytuj")}
                        </Button>
                    )}
                </Stack>

                {showDescriptionEditor && (
                    <Box sx={{ p: 1.5 }}>
                        <RichTextEditor
                            label={null}
                            value={description}
                            onValueChange={setDescription}
                            editable={isEditable}
                        />
                    </Box>
                )}
            </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box
            sx={{
                p: 1,
                pt: 0.5,
                pb: 0.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "grey.100",
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{ flexWrap: "wrap", rowGap: 1 }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {l10n.getString("document-questions", null, "_Pytania")}
                    </Typography>
                </Stack>

                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    gap: 1,
                }}>
                    <Button variant='outlined' size='small' onClick={handleAllNotApplicableClick}>
                        <Localized id='document-question-all-na'>Wszystkie N/A</Localized>
                    </Button>
                    <Button variant='outlined' size='small' onClick={handleAllNoClick}>
                        <Localized id='document-question-all-no'>Wszystkie Nie</Localized>
                    </Button>
                    <Button variant='outlined' size='small' onClick={handleAllYesClick}>
                        <Localized id='document-question-all-yes'>Wszystkie Tak</Localized>
                    </Button>
                </Box>
            </Stack>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <Stack divider={<Divider />} spacing={0.5} sx={{ pl: "2px", pr: 0.75 }}>
            {questionRows.map((q) => (
                <Box
                    key={q.id}
                    display="flex"
                    alignItems="left"
                    gap={2}
                    sx={{
                        px: 0.5,
                        borderRadius: 0.75,
                        "&:hover": { bgcolor: "action.hover" }
                    }}
                >
                    <LpField value={q.order} sx={{ marginTop: "3px" }} />
                    <Typography sx={{ flex: 1, marginTop: "3px", textAlign: "left" }}>
                        {q.text}
                    </Typography>
                    <ResultChips
                        value={answers[q.id] ?? "NONE"}
                        onChange={(val) => answer(val, q.id)}
                    />
                </Box>
            ))}
            {questionRows.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    <Localized id="document-question-empty-list">
                        <span>_empty</span>
                    </Localized>
                </Typography>
            )}
        </Stack>
        <Divider sx={{ my: 0.5 }} />
        {questionRows.length > 0 && (
            <Box
                sx={{
                    mt: 0.75,
                    width: "100%",
                    bgcolor: "grey.100",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    px: 1,
                    py: 0.4,
                }}
            >
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        display: "block",
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                    }}
                >
                    {l10n.getString("document-question-answered-count", null, "_Odpowiedziano")}
                    {": "}
                    {answeredCount} / {questionRows.length}
                </Typography>
            </Box>
        )}
        <Divider sx={{ my: 1 }} />
        <Paper variant="outlined" sx={{ p: 1, mb: 2, bgcolor: "grey.50", borderColor: "divider" }}>
            <Typography
                variant="subtitle1"
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, fontWeight: 700 }}
            >
                <AssessmentIcon fontSize="small" color="primary" />
                <Localized id="final-note">
                    <span>_final-note</span>
                </Localized>
            </Typography>
            <ResultChips value={finalAssessment} onChange={setFinalAssessment} />
        </Paper>
        <TextField
            label={l10n.getString("comment", null, "_comment")}
            multiline
            disabled={!isEditable}
            rows={2}
            fullWidth
            value={comment}
            onChange={(e) => setComment(e.target.value)}
        />
        {serverError && (
            <Alert severity="error" sx={{ mt: 2 }}>
                {serverError}
            </Alert>
        )}
    </>

    const titleCounter =
        mode === "create"
            ? `${total + 1}/${total + 1}`
            : index !== null
                ? `${index + 1}/${total}`
                : null

    return (
        <Dialog open={true} maxWidth="lg" fullWidth keepMounted={false}>
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "#f9f9f9",
                    color: "text.primary",
                    fontWeight: "bold",
                }}
            >
                <Box>{title}</Box>
                {titleCounter && <Box sx={{ ml: 'auto' }}>{titleCounter}</Box>}
            </DialogTitle>
            <DialogContent dividers>
                {isCommentable
                    ? <SectionComments
                        sectionId={sectionId}
                        contextType='analyzed_object'
                        contextGuid={analyzedObjectRow?.guid ?? ''}
                        component={<Box>{content}</Box>}
                    />
                    : content}
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
                {mode !== 'create' && total > 1 && (
                    <LoadingButton disabled={!index} variant="outlined" size='small' loading={saving} onClick={handleBackClick}>
                        <Localized id="back">Back</Localized>
                    </LoadingButton>
                )}
                <SaveButton disabled={!isEditable} loading={saving} onClick={saveAndClose} />
                <CancelButton loading={saving} onClick={onCancelClick} />
                {mode === 'edit' && <LoadingButton disabled={lastItem} variant="outlined" size='small' loading={saving} onClick={handleSaveAndNext}>
                    <Localized id="save-and-next">Save & next</Localized>
                </LoadingButton>}
                {mode === 'comment' && <LoadingButton disabled={lastItem} variant="outlined" size='small' loading={saving} onClick={() => analyzedObjectRow && next(analyzedObjectRow)}>
                    <Localized id="next">Next</Localized>
                </LoadingButton>}
            </DialogActions>
        </Dialog>
    );
};
